/**
 * RelatedPeople — inline Related-Persons strip rendered at the top of the
 * CIF record page.
 *
 * Data source fix (2026-04-21):
 *   Previously this component called `client.jxrl.custRelInq` → raw
 *   `Customer/CustRelInq`. The Core Director tenant returned HTTP 500 for
 *   every call (legacy SPFx never used `CustRelInq`; it always used
 *   `Customer/CustAcctRelInq` — see `spfx/.../admin/KillListPanel.tsx:540`
 *   and `memory/custinq-limitations.md`).  We now call the typed v3 Function
 *   route `/accounts/cust-acct-rel-inq/{cifId}` (`client.accounts.custAcctRelInq`)
 *   which proxies `Customer/CustAcctRelInq` and returns the CustAcctRelInqRs
 *   envelope with `AcctRec_CTypeArray` rows.
 *
 * BACKLOG-0870 (Bill 2026-07-13 r2): this strip used to show only account ids
 * + rel codes ("Acct 123 · JT") and, for a CIF with many accounts, "0 / none
 * on file" — because CustAcctRelInq returns account ids + rel codes, NOT the
 * related persons' names. We now chain: CustAcctRelInq (the CIF's accounts) →
 * client.accounts.parties(acctId, acctType) per account (the normalized
 * AcctRelSrch roster, BACKLOG-0794) → collect every OTHER customer on those
 * accounts, deduped by CIF, with their name + capacity (relLabel). That is the
 * real related-persons list.
 */
import { useEffect, useState } from 'react';
import { Button, EntityIcon, Pill } from '@/components/primitives';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useApi } from '../hooks/useApi';
import { useEmitEvent } from '../hooks/useEmitEvent';
import { REL_CODE_LABELS } from '../utils/formatting';

export interface RelatedPeopleProps {
  cifId: string;
}

/** A resolved related PERSON (deduped across all of the CIF's accounts). */
interface RelatedPerson {
  cifId: string;
  name: string;
  relCode: string;
  relLabel?: string;
  custType?: string;
  /** Sample account this relationship was seen on (for the tooltip). */
  onAcctId: string;
}

interface RelRow {
  acctId: string;
  acctType: string;
  relCode: string;
}

/** Cap parties() fan-out so a customer with dozens of accounts stays snappy. */
const MAX_ACCOUNTS_SCANNED = 12;

function extractRelationships(payload: unknown): RelRow[] {
  // Envelope from /accounts/cust-acct-rel-inq/{cifId}:
  //   { Success: true, Data: { CustAcctRelInqRs_MType: { AcctRec_CTypeArray: [ { AcctId:{AcctId,AcctType}, RelCode } ] } } }
  // We also tolerate raw jXRL shape (no envelope) and older camelCase.
  if (!payload || typeof payload !== 'object') return [];
  const anyP = payload as Record<string, unknown>;

  // Unwrap envelope → data node
  const dataNode =
    (anyP['Data'] as Record<string, unknown> | undefined) ??
    (anyP['data'] as Record<string, unknown> | undefined) ??
    anyP;

  // Find the AcctRec array under any of the common jXRL shapes.
  const candidates: unknown[] = [];
  const pushArr = (v: unknown): void => {
    if (Array.isArray(v)) candidates.push(...v);
    else if (v && typeof v === 'object') candidates.push(v);
  };

  const rsMType = (dataNode['CustAcctRelInqRs_MType'] as Record<string, unknown> | undefined) ?? undefined;
  if (rsMType) {
    pushArr(rsMType['AcctRec_CTypeArray']);
    pushArr(rsMType['AcctRecArray']);
  }
  pushArr(dataNode['CustAcctRelInqRecArray']);
  pushArr(dataNode['AcctRec_CTypeArray']);

  return candidates
    .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x))
    .map((r): RelRow => {
      // AcctId can be a raw string or { AcctId, AcctType } object
      const rawAcctId = r['AcctId'];
      let acctId = '';
      let acctType = '';
      if (typeof rawAcctId === 'string') {
        acctId = rawAcctId;
      } else if (rawAcctId && typeof rawAcctId === 'object') {
        const inner = rawAcctId as Record<string, unknown>;
        acctId = typeof inner['AcctId'] === 'string' ? (inner['AcctId'] as string) : '';
        acctType = typeof inner['AcctType'] === 'string' ? (inner['AcctType'] as string) : '';
      }
      if (!acctType && typeof r['AcctType'] === 'string') acctType = r['AcctType'] as string;
      const relCode =
        typeof r['RelCode'] === 'string' ? (r['RelCode'] as string) :
        typeof r['AcctRelCode'] === 'string' ? (r['AcctRelCode'] as string) : '';
      return { acctId, acctType, relCode };
    })
    // Primary rows (`P`) aren't "related people" — they're the current CIF's
    // own primary role. Filter them out so the strip only shows true relations.
    .filter((r): boolean => r.relCode !== '' && r.relCode !== 'P');
}

export function RelatedPeople({ cifId }: RelatedPeopleProps): JSX.Element {
  const { client, makeSignal } = useApi();
  const emit = useEmitEvent();
  const [people, setPeople] = useState<RelatedPerson[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const signal = makeSignal();
    let cancelled = false;
    void (async () => {
      try {
        const relRes = await client.accounts.custAcctRelInq(cifId, { signal });
        const anyRes = relRes as unknown as { success?: boolean; error?: string };
        if (anyRes?.success === false) {
          if (!cancelled) { setError(anyRes.error ?? 'CustAcctRelInq failed'); setPeople([]); }
          return;
        }
        const accts = extractRelationships(relRes);
        // Unique (acctId, acctType) to scan, capped.
        const seenAcct = new Set<string>();
        const toScan = accts.filter((a) => {
          const k = `${a.acctId}-${a.acctType}`;
          if (!a.acctId || seenAcct.has(k)) return false;
          seenAcct.add(k);
          return true;
        }).slice(0, MAX_ACCOUNTS_SCANNED);

        // Fan out parties() per account; collect every OTHER customer, deduped
        // by CIF (first capacity seen wins). Fail-soft per account.
        const byCif = new Map<string, RelatedPerson>();
        await Promise.all(toScan.map(async (a) => {
          try {
            const resp = await client.accounts.parties({ acctId: a.acctId, acctType: a.acctType }, { signal });
            for (const party of resp.parties ?? []) {
              if (!party.custId || party.custId === cifId) continue; // skip self
              if (byCif.has(party.custId)) continue;
              byCif.set(party.custId, {
                cifId: party.custId,
                name: party.name || party.comName || `CIF ${party.custId}`,
                relCode: party.relCode,
                relLabel: party.relLabel ?? undefined,
                custType: party.custType ?? undefined,
                onAcctId: a.acctId,
              });
            }
          } catch { /* fail-soft per account */ }
        }));

        if (!cancelled) setPeople([...byCif.values()]);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (!cancelled) setError(err instanceof Error ? err.message : 'Related-persons lookup failed');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cifId]);

  const onAdd = (): void => {
    emit({
      eventType: 'rcm.cust-rel.add-attempted',
      eventCategory: 'cif-account',
      payload: { cifId },
    });
    // TODO: open AddRelationshipDialog.
  };

  if (error) {
    return <div style={{ color: 'var(--warn-text)', fontSize: 12 }}>Related persons unavailable: {error}</div>;
  }
  if (people === null) {
    return <LoadingSpinner label="Loading related persons…" size="sm" />;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--sp-4)' }}>
      <strong style={{ fontSize: 12, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
        Related persons ({people.length})
      </strong>
      {people.length === 0 && (
        <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>none on file</span>
      )}
      {people.map((p) => (
        <span key={p.cifId} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-3)' }}
          title={`${p.relLabel ?? REL_CODE_LABELS[p.relCode] ?? p.relCode} · CIF ${p.cifId} · seen on acct ${p.onAcctId}`}>
          <EntityIcon type="relationship" code={p.relCode} title={p.relLabel ?? REL_CODE_LABELS[p.relCode] ?? p.relCode} />
          <span style={{ fontSize: 12, color: 'var(--ink-900)' }}>{p.name}</span>
          <Pill variant="muted">{p.relLabel ?? REL_CODE_LABELS[p.relCode] ?? p.relCode}</Pill>
        </span>
      ))}
      {/* "+ Add relationship" is hidden: it only emitted an event (no dialog, no Core write). Nothing unwired in
          front of pilot users (Bill 2026-10-17 maintenance review). */}
    </div>
  );
}
